import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import {
  Currency,
  DepositPageVarsBanner,
  UserBalance,
} from '@freelancer/datastore';
import { ExchangeRate } from '@freelancer/datastore/exchange-rates';
import { DepositMethodApi } from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { filter, map, startWith, switchMap, take } from 'rxjs/operators';
import {
  FinopsError,
  FinopsErrorType,
  FinopsMessage,
  FinopsResultStatus,
} from '../../common/finops-messaging/finops-messaging.types';

export enum DepositForm {
  NATIVE_CHARGE_TOGGLE = 'nativeChargeToggleFormControl',
  CONFIRMATION = 'confirmationFormGroup',
  RADIO = 'methodSelectionFormGroup',
  METHODS = 'methodsFormGroup',
}

export const methodSelectionRadioControl = 'RadioControl';

export enum ApplicationType {
  DepositPage,
  DepositModal,
  VerificationPage,
  VerificationModal,
}

export interface DepositModalItem {
  description: string;
  amount: number;
}

export interface DepositData {
  depositAmount: number;
  depositCurrency: Currency;
  paymentAmount: number;
  paymentCurrency: Currency;
}

export interface DepositContext {
  trackingToken?: string;
  backUrl?: string;
  banner: DepositPageVarsBanner;
  chargeAmount?: number;
  chargeCurrencyId?: number;
  items?: DepositModalItem[];
  applicationType?: ApplicationType;
  // TODO: get rid of checkBalance
  // it's only for migration period from the old deposit page
  checkBalance: boolean;
}

export interface MethodPaymentCurrency {
  methodId: string;
  currency: Currency;
}

export interface MethodSelectedCurrencies {
  [methodId: string]: Rx.Observable<Currency>;
}

export enum InterframeDepositResponseType {
  SUCCESS = 'success',
  PENDING = 'pending',
  CANCELLED = 'cancel',
  FAILURE = 'failure',
  ERROR = 'error',
  ERROR_DETAILED = 'error_detailed',
  ERROR_BIN = 'binError',
  ERROR_CART = 'cartError',
  ERROR_AUTH = 'authError',
}

export function mapValueToSelectedCurrencies(
  control: AbstractControl,
  methodSelectedCurrencies$: Rx.Observable<MethodSelectedCurrencies>,
) {
  return Rx.combineLatest(
    control.valueChanges.pipe(startWith(control.value)),
    methodSelectedCurrencies$,
  ).pipe(
    filter(([v, methodSelectedCurrencies]) => v in methodSelectedCurrencies),
    switchMap(([v, methodSelectedCurrencies]) =>
      methodSelectedCurrencies[v].pipe(
        map(currency => ({ currency, methodId: v })),
      ),
    ),
  );
}

export type MessageEventFilter = (event: MessageEvent) => boolean;
// Helper function for message handling
export function getFilteredMessageStream(
  messageEventFilter: MessageEventFilter,
): Rx.Observable<MessageEvent['data']> {
  return Rx.fromEvent(window, 'message').pipe(
    filter<any>(
      event =>
        (event.origin === window.location.origin ||
          window.location.hostname === 'localhost') &&
        messageEventFilter(event),
    ),
    map(event => event.data),
  );
}

export function listenForDepositResult(): Promise<MessageEvent['data']> {
  return getFilteredMessageStream(
    event =>
      !!event.data.type &&
      Object.values(InterframeDepositResponseType).includes(event.data.type),
  )
    .pipe(take(1))
    .toPromise();
}

export function handleInterframeDepositResult(
  data: any,
): FinopsMessage | undefined {
  switch (data.type) {
    case InterframeDepositResponseType.SUCCESS:
      return {
        finopsStatus: FinopsResultStatus.SUCCESS,
      };
    case InterframeDepositResponseType.ERROR_AUTH:
      return {
        errorType: FinopsErrorType.ERROR_AUTH,
      };
    case InterframeDepositResponseType.ERROR:
    case InterframeDepositResponseType.ERROR_DETAILED:
    case InterframeDepositResponseType.ERROR_BIN:
    case InterframeDepositResponseType.ERROR_CART:
    case InterframeDepositResponseType.FAILURE:
      return {
        errorMessage: data.human_msg
          ? data.human_msg
          : data.message
            ? data.message
            : '',
        errorCode: data.error_code,
        eventId: data.uuid,
        errorType: FinopsErrorType.ERROR,
      };
    case InterframeDepositResponseType.PENDING:
      return {
        finopsMessage: data.message ? data.message : '',
        finopsStatus: FinopsResultStatus.PENDING,
      };
    case InterframeDepositResponseType.CANCELLED:
      return {
        finopsStatus: FinopsResultStatus.INTERRUPTED,
      };
    default:
      return {
        errorType: FinopsErrorType.ERROR,
      };
  }
}

export function roundCurrency(amount: number, precision = 2): number {
  const multiplier = 10 ** precision;
  return Math.round(amount * multiplier) / multiplier;
}

export function parseDepositAPIError(err: HttpErrorResponse): FinopsError {
  let error = err.error && err.error.message ? err.error.message : err.message;
  try {
    // error message may be a json object
    error = JSON.parse(error);
  } catch (e) {
    return {
      errorType: FinopsErrorType.ERROR,
    };
  }

  return {
    errorMessage: error.error_msg ? error.error_msg : err.message,
    errorCode: error.error_code,
    eventId: error.uuid,
    errorType: FinopsErrorType.ERROR,
  };
}

export function redirectToBackUrl(depositContext: DepositContext | undefined) {
  const url = depositContext ? depositContext.backUrl || '' : '';
  window.location.href = url;
}

export function getExchangeRate(
  exchangeRates: ReadonlyArray<ExchangeRate>,
  fromCurrencyCode: string,
  toCurrencyCode: string,
): number {
  const fx = exchangeRates.find(v => v.id === toCurrencyCode);
  if (!fx) {
    throw new Error(`Exchange rate for ${fromCurrencyCode} not found!`);
  }
  const fxC = fx.from[fromCurrencyCode];
  if (!fxC) {
    throw new Error(
      `Exchange rate for ${fromCurrencyCode}-${toCurrencyCode} not found!`,
    );
  }
  return fxC;
}

export function getUserBalancesInCurrency(
  userBalances: ReadonlyArray<UserBalance>,
  exchangeRates: ReadonlyArray<ExchangeRate>,
  currency: Currency,
): number {
  return userBalances.reduce((acc, balance) => {
    const rate = getExchangeRate(
      exchangeRates,
      balance.currency.code,
      currency.code,
    );
    return acc + balance.amount * rate;
  }, 0);
}

export function getMethodLogoPath(method: DepositMethodApi | string) {
  return `payments/${method}.svg`;
}
